import { createSelector } from '@reduxjs/toolkit';
import { get } from 'lodash';
import type { RootState } from 'src/root/store';

import { esbApi } from '@modules/rtk/esb-api';
import type { UserOrganizationInfo } from '@modules/user/types';
import { emptyArr, eventsModuleName } from '@shared/constants';
import {
  isYouthMemberSel,
  organizationPositionsSel,
  personGuidSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions, newRolePermissions } from '@shared/newPermissions';
import { extractOrgGuidAndUserId } from '@shared/utils';

import reducers from './reducers';
import { createEventSlice } from './slice';
import { InvitedUser } from './types';

const moduleSel = (state: RootState): ReturnType<typeof reducers> =>
  get(state, eventsModuleName);

const createEventSliceSel = (state: RootState) =>
  state.createEventSlice || createEventSlice.getInitialState();

export const createEventFromSeriesSel = createSelector(
  createEventSliceSel,
  state => state,
);

export const selectedEventUsersSel = (state: RootState) =>
  moduleSel(state).selectedEventUsers as InvitedUser[];

export const hasInviteeChangesSel = createSelector(
  createEventSliceSel,
  ({ hasInviteeChanges }) => hasInviteeChanges,
);

export const eventToDeleteSel = createSelector(
  createEventSliceSel,
  ({ eventToDelete }) => eventToDelete,
);

/** Union of the permission sets granted by every role held in one organization */
const permissionsForRoles = (allRoles: string[] = []) =>
  allRoles.reduce<Partial<Record<string, boolean>>>(
    (perms, role) => ({
      ...perms,
      ...newRolePermissions[role as keyof typeof newRolePermissions],
    }),
    {},
  );

export const calendarUnitsWithPermsSel = createSelector(
  organizationPositionsSel,
  (organizations: UserOrganizationInfo[] = []) =>
    organizations.filter(
      org => !!permissionsForRoles(org.allRoles)[newPermissions.CRUD_EVENTS],
    ),
);

export const calendarUnitsWithSubOnlyPermsSel = createSelector(
  organizationPositionsSel,
  (organizations: UserOrganizationInfo[] = []) =>
    organizations.filter(org => {
      const perms = permissionsForRoles(org.allRoles);
      return (
        !perms[newPermissions.CRUD_EVENTS] &&
        !!perms[newPermissions.CRUD_SUB_EVENTS]
      );
    }),
);

export const userSubUnitsOrgGuidSel = createSelector(
  calendarUnitsWithSubOnlyPermsSel,
  ([subOnlyPermUnit]) =>
    extractOrgGuidAndUserId(subOnlyPermUnit?.organizationGuid || '').orgGuid,
);

const userSubUnitMembersSel = (state: RootState) => {
  const orgGuid = userSubUnitsOrgGuidSel(state);
  if (!orgGuid) return emptyArr;

  const endpoint = isYouthMemberSel(state)
    ? esbApi.endpoints.getSubUnitYouths
    : esbApi.endpoints.getSubUnitAdults;

  return endpoint.select({ orgGuid })(state)?.data?.members || emptyArr;
};

/**
 * Den/patrol assignments for the logged in user, read from the roster-v2 subUnit query.
 * So ENSURE you use the hook `useGetUserSubUnits` at least once.
 * Hooked into `allUserSubUnitsSel` to rely on this if no rosters are fetched.
 */
export const userSubUnitsSel = createSelector(
  userSubUnitMembersSel,
  personGuidSel,
  calendarUnitsWithSubOnlyPermsSel,
  (members, personGuid, [subOnlyPermUnit]) => {
    const unitId = Number(subOnlyPermUnit?.unitId);
    if (!unitId) return emptyArr;

    return members
      .filter(member => member.personGuid === personGuid)
      .flatMap(member => member.subUnitInfo)
      .filter(({ patrolId, denId }) => patrolId || denId)
      .map(({ patrolId, denId }) => ({
        id: patrolId || denId,
        unitId,
        patrolId,
        denId,
      }));
  },
);
